import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';

import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import Grid from '@/components/inputs/CustomGrid';
import CustomButton from '@/components/inputs/CustomButton';
import CustomTextField from '@/components/inputs/CustomTextField';
import useLoading from '@/components/loading/hooks/useLoading';
import useSnackbar from '@/components/dialogs/snackbars/hooks/useSnackbar';
import useError from '@/components/validations/hooks/useError';
import Connections, { ApiEndpoints } from '@/components/connections/Connections';

const validations = {
  name: { type: 'required', text: 'field.notValidOrRequired' },
};

const defaultValues = {
  name: '',
  eik: '',
  bulstat: '',
  vatId: '',
  accountablePerson: '',
  email: '',
  phone: '',
  address: { street: '', city: '', zip: '', country: '' },
  bankDetails: { iban: '', bic: '', bankName: '' },
  settings: {
    currency: 'EUR',
    defaultLanguage: 'bg',
    invoicePrefix: '',
    emailForCopy: '',
    paymentTermDays: 14,
    vat: { defaultVat: 20, vatRegistered: true, art97a: false, noVatReason: '' },
  },
};

const SectionTitle = ({ children }) => (
  <h2 className="text-base font-semibold mb-3">{children}</h2>
);

const CompanyForm = ({ companyId: companyIdProp, embedded = false }) => {
  const params = useParams();
  const companyId = companyIdProp || params.companyId;
  const isEdit = !!companyId;
  const [values, setValues] = useState(defaultValues);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, Loading, setLoading } = useLoading(isEdit);
  const { createSuccessSnackbar, createErrorSnackbar } = useSnackbar();
  const { isError, getActivateError } = useError({ values, validations });

  useEffect(() => {
    if (isEdit) {
      const loadCompany = async () => {
        const res = await Connections.postRequest(ApiEndpoints.companyGet, { companyId });
        if (res.ok) {
          const c = res.data.company;
          setValues({
            name: c.name || '',
            eik: c.eik || '',
            bulstat: c.bulstat || '',
            vatId: c.vatId || '',
            accountablePerson: c.accountablePerson || '',
            email: c.email || '',
            phone: c.phone || '',
            address: c.address || defaultValues.address,
            bankDetails: c.bankDetails || defaultValues.bankDetails,
            settings: {
              currency: c.settings?.currency || 'EUR',
              defaultLanguage: c.settings?.defaultLanguage || 'bg',
              invoicePrefix: c.settings?.invoicePrefix || '',
              emailForCopy: c.settings?.emailForCopy || '',
              paymentTermDays: c.settings?.paymentTermDays ?? 14,
              vat: {
                defaultVat: c.settings?.vat?.defaultVat ?? 20,
                vatRegistered: c.settings?.vat?.vatRegistered !== false,
                art97a: c.settings?.vat?.art97a === true,
                noVatReason: c.settings?.vat?.noVatReason || '',
              },
            },
          });
        }
        setLoading(false);
      };
      loadCompany().then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleChange = ({ name, value }) => {
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = ({ name, value }) => {
    setValues(prev => ({ ...prev, address: { ...prev.address, [name]: value } }));
  };

  const handleBankChange = ({ name, value }) => {
    setValues(prev => ({ ...prev, bankDetails: { ...prev.bankDetails, [name]: value } }));
  };

  const handleSettingsChange = ({ name, value }) => {
    setValues(prev => ({ ...prev, settings: { ...prev.settings, [name]: value } }));
  };

  const handleVatChange = ({ name, value }) => {
    setValues(prev => ({
      ...prev,
      settings: { ...prev.settings, vat: { ...prev.settings.vat, [name]: value } },
    }));
  };

  // Art. 97a registration always means VAT is not charged on the invoices
  const handleArt97aChange = value => {
    setValues(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        vat: {
          ...prev.settings.vat,
          art97a: value,
          ...(value && { vatRegistered: false }),
        },
      },
    }));
  };

  const handleSave = async () => {
    const error = getActivateError();
    if (error) return;

    setLoading(true);
    const endpoint = isEdit ? ApiEndpoints.companyUpdate : ApiEndpoints.companyCreate;
    const params = isEdit ? { companyId, ...values } : values;
    const res = await Connections.postRequest(endpoint, params);

    if (res.ok) {
      createSuccessSnackbar(t(isEdit ? 'company.updated' : 'company.created'));
      if (!embedded) navigate('/companies');
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  if (loading) return <Loading />;

  const { address, bankDetails, settings } = values;

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>{t('app.title')} | {t(isEdit ? 'company.edit' : 'company.create')}</title>
        </Helmet>
      )}
      {!embedded && (
        <h1 className="text-2xl font-semibold mb-6">
          {t(isEdit ? 'company.edit' : 'company.create')}
        </h1>
      )}

      <SectionTitle>{t('company.info')}</SectionTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomTextField
            fieldName="name" label={t('company.name')} value={values.name}
            onChange={handleChange} fullWidth error={isError('name')}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="eik" label={t('eik')} value={values.eik} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="bulstat" label={t('bulstat')} value={values.bulstat} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="vatId" label={t('vatId')} value={values.vatId} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField
            fieldName="accountablePerson" label={t('accountablePerson')} value={values.accountablePerson}
            onChange={handleChange} fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="email" label={t('email')} value={values.email} onChange={handleChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="phone" label={t('phone')} value={values.phone} onChange={handleChange} fullWidth />
        </Grid>
      </Grid>

      <Separator className="my-6" />
      <SectionTitle>{t('address')}</SectionTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomTextField fieldName="street" label={t('address.street')} value={address.street} onChange={handleAddressChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="city" label={t('address.city')} value={address.city} onChange={handleAddressChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomTextField fieldName="zip" label={t('address.zip')} value={address.zip} onChange={handleAddressChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={1}>
          <CustomTextField fieldName="country" label={t('address.country')} value={address.country} onChange={handleAddressChange} fullWidth />
        </Grid>
      </Grid>

      <Separator className="my-6" />
      <SectionTitle>{t('bankDetails')}</SectionTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="iban" label={t('bank.iban')} value={bankDetails.iban} onChange={handleBankChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="bic" label={t('bank.bic')} value={bankDetails.bic} onChange={handleBankChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={4}>
          <CustomTextField fieldName="bankName" label={t('bank.name')} value={bankDetails.bankName} onChange={handleBankChange} fullWidth />
        </Grid>
      </Grid>

      <Separator className="my-6" />
      <SectionTitle>{t('defaults')}</SectionTitle>
      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <CustomTextField fieldName="currency" label={t('currency')} value={settings.currency} onChange={handleSettingsChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomTextField fieldName="defaultLanguage" label={t('language')} value={settings.defaultLanguage} onChange={handleSettingsChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomTextField fieldName="invoicePrefix" label={t('invoice.prefix')} value={settings.invoicePrefix} onChange={handleSettingsChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomTextField fieldName="emailForCopy" label={t('emailForCopy')} value={settings.emailForCopy} onChange={handleSettingsChange} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <CustomTextField
            fieldName="paymentTermDays" label={t('paymentTermDays')} value={settings.paymentTermDays}
            onChange={handleSettingsChange} fullWidth type="number"
          />
        </Grid>
        <Grid item xs={12} md={1}>
          <CustomTextField
            fieldName="defaultVat" label={t('vat.default')} value={settings.vat.defaultVat}
            onChange={({ value }) => handleVatChange({ name: 'defaultVat', value })}
            fullWidth type="number"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12} md={4}>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none h-full">
            <Checkbox
              checked={settings.vat.vatRegistered && !settings.vat.art97a}
              disabled={settings.vat.art97a}
              onCheckedChange={checked => handleVatChange({ name: 'vatRegistered', value: checked === true })}
            />
            {t('vat.registered')}
          </label>
        </Grid>
        <Grid item xs={12} md={4}>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none h-full">
            <Checkbox
              checked={settings.vat.art97a}
              onCheckedChange={checked => handleArt97aChange(checked === true)}
            />
            {t('vat.art97a')}
          </label>
        </Grid>
        {(!settings.vat.vatRegistered || settings.vat.art97a) && (
          <Grid item xs={12} md={8}>
            <CustomTextField
              fieldName="noVatReason" label={t('vat.noVatReason')} value={settings.vat.noVatReason}
              onChange={handleVatChange} fullWidth placeholder="чл. 113, ал. 9 от ЗДДС"
            />
            {settings.vat.art97a && (
              <p className="text-xs text-gray-500 mt-1">{t('vat.art97aHint')}</p>
            )}
          </Grid>
        )}
      </Grid>

      <div className="flex justify-end mt-8 gap-3">
        {!embedded && (
          <CustomButton variant="outlined" onClick={() => navigate('/companies')}>
            {t('cancel')}
          </CustomButton>
        )}
        <CustomButton onClick={handleSave}>
          {t(isEdit ? 'save' : 'create')}
        </CustomButton>
      </div>
    </>
  );
};

export default CompanyForm;
